import React from "react";
import { Link } from "react-router-dom";
import Container from "Components/Container/Container";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";

const SERVER_URL = BASE_URL.replace(/\/api$/, "");
const imgUrl = (p) => (!p ? null : p.startsWith("http") ? p : `${SERVER_URL}/${p}`);

const Avatar = ({ inst, size }) => {
  const img = imgUrl(inst?.profileImage);
  return (
    <div
      className="rounded-full overflow-hidden ring-2 ring-primary/30 shrink-0"
      style={{ width: size, height: size }}
    >
      {img ? (
        <img src={img} alt={inst?.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-lightGrey flex items-center justify-center text-white font-bold text-xl">
          {inst?.name?.charAt(0) || "I"}
        </div>
      )}
    </div>
  );
};

// Course-landing instructor section. Each instructor is an equal card that
// stretches to fill the row (no lonely cluster in an empty panel). A single
// instructor gets a wider feature card with their bio.
const Instructor = ({ instructors = [] }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const list = (instructors || []).filter(Boolean);
  if (list.length === 0) return null;

  const single = list.length === 1;
  const cardBase =
    "group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-grey to-grey/40 border border-white/10 hover:border-primary/40 hover:from-grey hover:to-grey/60 transition-all duration-300";

  return (
    <section className="pt-10 sm:pt-14 lg:pt-primary">
      <Container>
        {/* Header label + hairline rule spanning the width */}
        <div className="flex items-center gap-4 mb-5">
          <p className="text-primary text-xs uppercase tracking-[0.16em] font-semibold whitespace-nowrap">
            {single
              ? t("courses.instructor") || "Instructor"
              : `${t("courses.instructors") || "Instructors"} · ${list.length}`}
          </p>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        {single ? (
          <Link to={`/instructor-profile/${list[0]?.slug}`} className={`${cardBase} flex-col sm:flex-row sm:items-center p-6 sm:p-8`}>
            <Avatar inst={list[0]} size={104} />
            <div className="flex-1 min-w-0 text-center sm:text-start">
              <h3 className="text-white font-bold text-2xl group-hover:text-primary transition-colors">
                {list[0]?.name}
              </h3>
              {list[0]?.bio && (
                <p className="text-white/60 text-sm mt-2 line-clamp-2 max-w-2xl">{list[0].bio}</p>
              )}
            </div>
            <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm whitespace-nowrap">
              {t("courses.view_profile")}
              <ArrowRight size={16} className={isRTL ? "rotate-180" : ""} />
            </span>
          </Link>
        ) : (
          <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
            {list.map((inst, i) => (
              <Link key={inst._id || i} to={`/instructor-profile/${inst?.slug}`} className={`${cardBase} p-4 sm:p-5`}>
                <Avatar inst={inst} size={64} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg leading-tight group-hover:text-primary transition-colors truncate">
                    {inst?.name}
                  </h3>
                  {inst?.bio && (
                    <p className="text-white/50 text-xs mt-1 line-clamp-1">{inst.bio}</p>
                  )}
                </div>
                <ArrowRight
                  size={18}
                  className={`text-white/30 group-hover:text-primary transition-colors shrink-0 ${isRTL ? "rotate-180" : ""}`}
                />
              </Link>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
};

export default Instructor;
